/**
 * Runtime configuration, fetched from /config.json before the app renders so
 * one build can be deployed to any stack (dev/prod) — the infra stack uploads
 * config.json next to index.html. See public/config.example.json.
 */
export interface RuntimeConfig {
  region: string;
  /** Base URL of the callable API (the REST API stage or its custom domain). */
  apiUrl: string;
  userPoolId: string;
  userPoolClientId: string;
  /** Managed login host, `<prefix>.auth.<region>.amazoncognito.com` (no scheme). */
  cognitoDomain: string;
  /** Cognito identity provider name (e.g. 'EntraID'); skips the provider chooser. */
  identityProvider?: string;
}

let config: RuntimeConfig | undefined;

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

const REQUIRED = ['region', 'apiUrl', 'userPoolId', 'userPoolClientId', 'cognitoDomain'] as const;

/**
 * Validates the parsed JSON (the shape infra/lib/migration.stack.ts publishes);
 * throws a readable message naming the bad fields.
 */
export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const missing = REQUIRED.filter((key) => !isNonEmptyString(obj[key]));
  if (missing.length) {
    throw new Error(`config.json is missing ${missing.join(', ')}`);
  }
  const str = (key: (typeof REQUIRED)[number]) => (obj[key] as string).trim();
  return {
    region: str('region'),
    // Callable URLs are `${apiUrl}/${name}`, so no trailing slash.
    apiUrl: str('apiUrl').replace(/\/+$/, ''),
    userPoolId: str('userPoolId'),
    userPoolClientId: str('userPoolClientId'),
    // Accept the bare host infra writes, or a full URL.
    cognitoDomain: str('cognitoDomain').replace(/^https?:\/\//, '').replace(/\/+$/, ''),
    ...(isNonEmptyString(obj['identityProvider'])
      ? { identityProvider: obj['identityProvider'].trim() }
      : {}),
  };
}

/** OIDC issuer of the user pool. */
export function cognitoAuthority(c: RuntimeConfig): string {
  return `https://cognito-idp.${c.region}.amazonaws.com/${c.userPoolId}`;
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) throw new Error(`Could not load /config.json (HTTP ${res.status})`);
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    throw new Error('/config.json is not valid JSON');
  }
  config = parseRuntimeConfig(raw);
  return config;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!config) throw new Error('Runtime config not loaded yet');
  return config;
}
